import {
  ArrowLeft,
  Check,
  Database,
  Loader2,
  RefreshCcw,
  Save,
  Shield,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "../lib/api";
import { APP_ROUTES } from "../routes";
import type {
  AgentRecord,
  ColumnSchema,
  DataType,
  DatabaseProvider,
  PostgresAgentConfig,
  SchemaAccessRole,
  TableSchema,
} from "../types";

type ParsedConnection = {
  host?: string;
  port?: string;
  database?: string;
  username?: string;
  password?: string;
  queryParams?: string;
};

type ConnectionMode = "params" | "string";
type ActiveTab = "connection" | "schema";

type SchemaColumnDraft = ColumnSchema & {
  enabled: boolean;
  rawDataType: string;
};

type SchemaTableDraft = {
  name: string;
  type: string;
  enabled: boolean;
  defaultLimit: number;
  description: string;
  columns: SchemaColumnDraft[];
};

type SchemaTablesByRole = Record<SchemaAccessRole, SchemaTableDraft[]>;

type SchemaResponse = {
  schema: Array<{
    name: string;
    type: string;
    columns: Array<{
      column_name: string;
      dataType: string;
      mappedType: DataType;
    }>;
  }>;
};

const SCHEMA_ACCESS_ROLES: SchemaAccessRole[] = ["admin", "member"];

const PROVIDER_DEFAULTS: Record<
  DatabaseProvider,
  {
    port: string;
    protocol: string;
    label: string;
    usernamePlaceholder: string;
  }
> = {
  postgres: {
    port: "5432",
    protocol: "postgresql:",
    label: "PostgreSQL",
    usernamePlaceholder: "postgres",
  },
  mysql: {
    port: "3306",
    protocol: "mysql:",
    label: "MySQL",
    usernamePlaceholder: "root",
  },
};

const createEmptyRoleTables = (): SchemaTablesByRole => ({
  admin: [],
  member: [],
});

const getProviderLabel = (provider: DatabaseProvider): string =>
  PROVIDER_DEFAULTS[provider].label;

const getRoleLabel = (role: SchemaAccessRole): string =>
  role === "admin" ? "Admins" : "Members";

const inferProviderFromConnectionString = (
  connectionString: string,
): DatabaseProvider | null => {
  if (!connectionString) {
    return null;
  }

  const normalized = connectionString.toLowerCase();
  if (normalized.startsWith("mysql://")) {
    return "mysql";
  }

  if (
    normalized.startsWith("postgresql://") ||
    normalized.startsWith("postgres://")
  ) {
    return "postgres";
  }

  return null;
};

const parseConnectionString = (
  provider: DatabaseProvider,
  connectionString: string,
): ParsedConnection | null => {
  const inferredProvider = inferProviderFromConnectionString(connectionString);
  if (!connectionString || (inferredProvider && inferredProvider !== provider)) {
    return null;
  }

  try {
    const url = new URL(connectionString);
    return {
      host: url.hostname || undefined,
      port: url.port || PROVIDER_DEFAULTS[provider].port,
      database: url.pathname.replace(/^\//, "") || undefined,
      username: url.username || undefined,
      password: url.password || undefined,
      queryParams: url.search || undefined,
    };
  } catch {
    return null;
  }
};

const buildConnectionString = (
  provider: DatabaseProvider,
  parsed: ParsedConnection,
): string => {
  if (!parsed.host) {
    return "";
  }

  const auth =
    parsed.username || parsed.password
      ? `${parsed.username ?? ""}${parsed.password ? `:${parsed.password}` : ""}@`
      : "";
  const defaultPort = PROVIDER_DEFAULTS[provider].port;
  const port = parsed.port && parsed.port !== defaultPort ? `:${parsed.port}` : "";
  const database = parsed.database ? `/${parsed.database}` : "";
  const queryParams = parsed.queryParams ?? "";

  return `${PROVIDER_DEFAULTS[provider].protocol}//${auth}${parsed.host}${port}${database}${queryParams}`;
};

const getDefaultColumnFlags = (dataType: DataType) => ({
  filterable: dataType !== "json",
  searchable: dataType === "string",
  sortable: dataType !== "json" && dataType !== "boolean",
});

const serializeDraftTables = (tables: SchemaTableDraft[]): TableSchema[] =>
  tables
    .filter((table) => table.enabled)
    .map<TableSchema>((table) => ({
      name: table.name,
      description: table.description,
      defaultLimit: table.defaultLimit,
      columns: table.columns
        .filter((column) => column.enabled)
        .map<ColumnSchema>(({ enabled: _enabled, rawDataType: _rawDataType, ...column }) => ({
          ...column,
        })),
    }))
    .filter((table) => table.columns.length > 0);

const getConfiguredTablesForRole = (
  config: PostgresAgentConfig,
  role: SchemaAccessRole,
): TableSchema[] => {
  const roleTables = config.roleAccess?.[role];
  if (Array.isArray(roleTables)) {
    return roleTables;
  }

  return Array.isArray(config.tables) ? config.tables : [];
};

const fromConfiguredTables = (tables: TableSchema[]): SchemaTableDraft[] =>
  tables.map((table) => ({
    name: table.name,
    type: "TABLE",
    enabled: true,
    defaultLimit: table.defaultLimit ?? 25,
    description: table.description ?? "",
    columns: table.columns.map((column) => ({
      ...column,
      enabled: true,
      rawDataType: column.dataType,
    })),
  }));

const fromSavedConfig = (config: PostgresAgentConfig): SchemaTablesByRole => ({
  admin: fromConfiguredTables(getConfiguredTablesForRole(config, "admin")),
  member: fromConfiguredTables(getConfiguredTablesForRole(config, "member")),
});

const mergeSchemaWithTables = (
  response: SchemaResponse,
  configuredTables: TableSchema[],
): SchemaTableDraft[] => {
  const configuredTableMap = new Map(configuredTables.map((table) => [table.name, table]));

  return response.schema.map((table) => {
    const configuredTable = configuredTableMap.get(table.name);
    const configuredColumns = new Map(
      (configuredTable?.columns ?? []).map((column) => [column.name, column]),
    );

    return {
      name: table.name,
      type: table.type,
      enabled: Boolean(configuredTable),
      defaultLimit: configuredTable?.defaultLimit ?? 25,
      description: configuredTable?.description ?? "",
      columns: table.columns.map((column) => {
        const configuredColumn = configuredColumns.get(column.column_name);
        const defaults = getDefaultColumnFlags(column.mappedType);

        return {
          name: column.column_name,
          dataType: configuredColumn?.dataType ?? column.mappedType,
          description: configuredColumn?.description ?? "",
          filterable: configuredColumn?.filterable ?? defaults.filterable,
          searchable: configuredColumn?.searchable ?? defaults.searchable,
          sortable: configuredColumn?.sortable ?? defaults.sortable,
          enabled: Boolean(configuredColumn),
          rawDataType: column.dataType,
        };
      }),
    };
  });
};

const mergeSchemaWithConfig = (
  response: SchemaResponse,
  config: PostgresAgentConfig,
): SchemaTablesByRole => ({
  admin: mergeSchemaWithTables(response, getConfiguredTablesForRole(config, "admin")),
  member: mergeSchemaWithTables(response, getConfiguredTablesForRole(config, "member")),
});

const serializeConfig = (
  provider: DatabaseProvider,
  connectionString: string,
  roleTables: SchemaTablesByRole,
): PostgresAgentConfig => {
  const adminTables = serializeDraftTables(roleTables.admin);
  const memberTables = serializeDraftTables(roleTables.member);

  return {
    provider,
    connectionString,
    tables: adminTables,
    roleAccess: {
      admin: adminTables,
      member: memberTables,
    },
  };
};

export function AgentPostgresIntegrationPage() {
  const navigate = useNavigate();
  const { agentId } = useParams();
  const [agent, setAgent] = useState<AgentRecord | null>(null);
  const [provider, setProvider] = useState<DatabaseProvider>("postgres");
  const [connectionMode, setConnectionMode] = useState<ConnectionMode>("params");
  const [activeTab, setActiveTab] = useState<ActiveTab>("connection");
  const [activeSchemaRole, setActiveSchemaRole] = useState<SchemaAccessRole>("admin");
  const [parsedConnection, setParsedConnection] = useState<ParsedConnection>({});
  const [connectionInput, setConnectionInput] = useState("");
  const [schemaTablesByRole, setSchemaTablesByRole] =
    useState<SchemaTablesByRole>(createEmptyRoleTables());
  const [expandedTables, setExpandedTables] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [cacheBursting, setCacheBursting] = useState(false);
  const [schemaSearch, setSchemaSearch] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<{
    kind: "success" | "error";
    message: string;
  } | null>(null);

  const providerLabel = getProviderLabel(provider);
  const currentRoleTables = schemaTablesByRole[activeSchemaRole];

  useEffect(() => {
    const loadAgent = async () => {
      if (!agentId) {
        setError("Agent ID is required");
        setLoading(false);
        return;
      }

      try {
        const nextAgent = await apiRequest<AgentRecord>(`/auth/agents/${agentId}`);
        const nextConnection = nextAgent.postgresConfig.connectionString ?? "";
        const nextProvider =
          nextAgent.postgresConfig.provider ??
          inferProviderFromConnectionString(nextConnection) ??
          "postgres";
        const parsed = parseConnectionString(nextProvider, nextConnection);

        setAgent(nextAgent);
        setProvider(nextProvider);
        setConnectionInput(nextConnection);
        setParsedConnection(parsed ?? {});
        setConnectionMode(parsed ? "params" : "string");
        setSchemaTablesByRole(fromSavedConfig(nextAgent.postgresConfig));
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : "Failed to load agent");
      } finally {
        setLoading(false);
      }
    };

    void loadAgent();
  }, [agentId]);

  const visibleTables = useMemo(() => {
    if (!schemaSearch.trim()) {
      return currentRoleTables;
    }

    const query = schemaSearch.trim().toLowerCase();
    return currentRoleTables.filter(
      (table) =>
        table.name.toLowerCase().includes(query) ||
        table.columns.some((column) => column.name.toLowerCase().includes(query)),
    );
  }, [currentRoleTables, schemaSearch]);

  const updateRoleTables = (
    role: SchemaAccessRole,
    updater: (tables: SchemaTableDraft[]) => SchemaTableDraft[],
  ) => {
    setSchemaTablesByRole((current) => ({
      ...current,
      [role]: updater(current[role]),
    }));
  };

  const updateCurrentRoleTables = (
    updater: (tables: SchemaTableDraft[]) => SchemaTableDraft[],
  ) => {
    updateRoleTables(activeSchemaRole, updater);
  };

  const updateParsedConnection = (key: keyof ParsedConnection, value: string) => {
    const nextParsed = {
      ...parsedConnection,
      [key]: value || undefined,
    };
    setParsedConnection(nextParsed);
    setConnectionInput(buildConnectionString(provider, nextParsed));
    setConnectionStatus(null);
  };

  const handleProviderChange = (nextProvider: DatabaseProvider) => {
    if (nextProvider === provider) {
      return;
    }

    const nextParsed = {
      ...parsedConnection,
      port:
        parsedConnection.port === PROVIDER_DEFAULTS[provider].port || !parsedConnection.port
          ? PROVIDER_DEFAULTS[nextProvider].port
          : parsedConnection.port,
    };

    setProvider(nextProvider);
    setParsedConnection(nextParsed);
    setConnectionInput(
      connectionMode === "params"
        ? buildConnectionString(nextProvider, nextParsed)
        : connectionInput.replace(
            /^[a-z0-9+.-]+:\/\//i,
            `${PROVIDER_DEFAULTS[nextProvider].protocol}//`,
          ),
    );
    setConnectionStatus(null);
  };

  const handleConnectionStringChange = (value: string) => {
    const inferredProvider = inferProviderFromConnectionString(value) ?? provider;
    if (inferredProvider !== provider) {
      setProvider(inferredProvider);
    }

    setConnectionInput(value);
    setParsedConnection(parseConnectionString(inferredProvider, value) ?? {});
    setConnectionStatus(null);
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);

    try {
      const response = await apiRequest<{ message: string }>("/auth/database/test_connection", {
        method: "POST",
        body: JSON.stringify({ provider, connectionString: connectionInput }),
      });
      setConnectionStatus({ kind: "success", message: response.message });
    } catch (connectionError) {
      setConnectionStatus({
        kind: "error",
        message:
          connectionError instanceof Error
            ? connectionError.message
            : `Failed to connect to ${providerLabel}.`,
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleLoadSchema = async () => {
    setSchemaLoading(true);

    try {
      const response = await apiRequest<SchemaResponse>("/auth/database/get_schema", {
        method: "POST",
        body: JSON.stringify({ provider, connectionString: connectionInput }),
      });
      setSchemaTablesByRole(
        mergeSchemaWithConfig(
          response,
          serializeConfig(provider, connectionInput, schemaTablesByRole),
        ),
      );
      setActiveTab("schema");
    } catch (schemaError) {
      setConnectionStatus({
        kind: "error",
        message:
          schemaError instanceof Error
            ? schemaError.message
            : "Failed to fetch database schema.",
      });
    } finally {
      setSchemaLoading(false);
    }
  };

  const handleBurstCache = async () => {
    setCacheBursting(true);

    try {
      const response = await apiRequest<{ message: string }>("/auth/database/burst_cache", {
        method: "POST",
        body: JSON.stringify({ agentId, provider, connectionString: connectionInput }),
      });
      setConnectionStatus({ kind: "success", message: response.message });
    } catch (cacheError) {
      setConnectionStatus({
        kind: "error",
        message:
          cacheError instanceof Error
            ? cacheError.message
            : `Failed to clear ${providerLabel} cache.`,
      });
    } finally {
      setCacheBursting(false);
    }
  };

  const handleSave = async () => {
    if (!agent) {
      return;
    }

    setSaving(true);
    setStatus(null);
    setError(null);

    try {
      const postgresConfig = serializeConfig(provider, connectionInput, schemaTablesByRole);
      await apiRequest<AgentRecord>("/auth/saveAgent", {
        method: "POST",
        body: JSON.stringify({
          agent: {
            id: agent.id,
            name: agent.name,
            description: agent.description ?? "",
            instructions: agent.instructions,
            model: agent.model,
            postgresConfig,
          },
        }),
      });
      setStatus(`${providerLabel} integration saved.`);
    } catch (saveError) {
      setError(
        saveError instanceof Error ? saveError.message : "Failed to save integration",
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleTableExpanded = (tableName: string) => {
    const key = `${activeSchemaRole}:${tableName}`;
    setExpandedTables((current) =>
      current.includes(key)
        ? current.filter((value) => value !== key)
        : [...current, key],
    );
  };

  const toggleTableEnabled = (tableName: string, enabled: boolean) => {
    updateCurrentRoleTables((current) =>
      current.map((table) =>
        table.name === tableName
          ? {
              ...table,
              enabled,
              columns: table.columns.map((column) => ({ ...column, enabled })),
            }
          : table,
      ),
    );
  };

  const toggleColumnEnabled = (
    tableName: string,
    columnName: string,
    enabled: boolean,
  ) => {
    updateCurrentRoleTables((current) =>
      current.map((table) => {
        if (table.name !== tableName) {
          return table;
        }

        const nextColumns = table.columns.map((column) =>
          column.name === columnName ? { ...column, enabled } : column,
        );

        return {
          ...table,
          enabled: nextColumns.some((column) => column.enabled),
          columns: nextColumns,
        };
      }),
    );
  };

  const updateColumnFlag = (
    tableName: string,
    columnName: string,
    key: "filterable" | "searchable" | "sortable",
    value: boolean,
  ) => {
    updateCurrentRoleTables((current) =>
      current.map((table) =>
        table.name === tableName
          ? {
              ...table,
              columns: table.columns.map((column) =>
                column.name === columnName ? { ...column, [key]: value } : column,
              ),
            }
          : table,
      ),
    );
  };

  const getVisibleColumns = (table: SchemaTableDraft): SchemaColumnDraft[] => {
    const query = schemaSearch.trim().toLowerCase();
    if (!query || table.name.toLowerCase().includes(query)) {
      return table.columns;
    }

    return table.columns.filter((column) => column.name.toLowerCase().includes(query));
  };

  if (loading) {
    return (
      <div className="p-4 px-6 pb-10 md:px-16">
        <div className="pt-4 text-sm text-slate-500 md:pt-6">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-4 px-6 pb-10 md:px-16">
      <button
        onClick={() => navigate(`${APP_ROUTES.agents}?agentId=${agentId}`)}
        className="mb-4 flex items-center gap-2 rounded-md px-2 py-1 text-sm text-slate-600 hover:bg-slate-100"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Agent
      </button>

      <div className="mb-6 flex items-center gap-3">
        <span className="text-slate-500">
          <Database className="h-6 w-6" />
        </span>
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Configure Database</h2>
          <p className="text-sm text-slate-500">Set up the integration for this agent.</p>
        </div>
      </div>

      {error ? (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {status ? (
        <div className="mb-4 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {status}
        </div>
      ) : null}

      <div className="mb-4 inline-flex rounded-md border border-slate-200 bg-white p-1">
        <button
          onClick={() => setActiveTab("connection")}
          className={`rounded-md px-3 py-2 text-sm font-medium ${
            activeTab === "connection"
              ? "bg-slate-900 text-white"
              : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          Connection
        </button>
        <button
          onClick={() => setActiveTab("schema")}
          className={`rounded-md px-3 py-2 text-sm font-medium ${
            activeTab === "schema"
              ? "bg-slate-900 text-white"
              : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          Schema Access
        </button>
      </div>

      {activeTab === "connection" ? (
        <section className="mx-auto max-w-3xl rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-6 py-4">
            <div className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
              <Database className="h-6 w-6 text-blue-600" />
              Database Connection
            </div>
            <p className="mt-1 text-sm text-slate-500">
              Connect a PostgreSQL or MySQL database to use as a data source.
            </p>
          </div>

          <div className="space-y-6 px-6 py-6">
            <div>
              <span className="mb-2 block text-sm font-medium text-slate-700">Provider</span>
              <div className="grid w-full grid-cols-2 rounded-md border border-slate-200 bg-slate-50 p-1">
                {(["postgres", "mysql"] as DatabaseProvider[]).map((option) => (
                  <button
                    key={option}
                    onClick={() => handleProviderChange(option)}
                    className={`rounded-md px-3 py-2 text-sm font-medium ${
                      provider === option
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-600"
                    }`}
                  >
                    {getProviderLabel(option)}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid w-full grid-cols-2 rounded-md border border-slate-200 bg-slate-50 p-1">
              <button
                onClick={() => setConnectionMode("params")}
                className={`rounded-md px-3 py-2 text-sm font-medium ${
                  connectionMode === "params"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-600"
                }`}
              >
                Individual Parameters
              </button>
              <button
                onClick={() => setConnectionMode("string")}
                className={`rounded-md px-3 py-2 text-sm font-medium ${
                  connectionMode === "string"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-600"
                }`}
              >
                Connection String
              </button>
            </div>

            {connectionMode === "params" ? (
              <div className="grid gap-4 md:grid-cols-2">
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-slate-700">Host</span>
                  <input
                    value={parsedConnection.host ?? ""}
                    onChange={(event) => updateParsedConnection("host", event.target.value)}
                    placeholder="localhost"
                    className="w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-200"
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-slate-700">Port</span>
                  <input
                    value={parsedConnection.port ?? PROVIDER_DEFAULTS[provider].port}
                    onChange={(event) => updateParsedConnection("port", event.target.value)}
                    placeholder={PROVIDER_DEFAULTS[provider].port}
                    className="w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-200"
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-slate-700">
                    Database
                  </span>
                  <input
                    value={parsedConnection.database ?? ""}
                    onChange={(event) =>
                      updateParsedConnection("database", event.target.value)
                    }
                    placeholder="mydatabase"
                    className="w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-200"
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-slate-700">
                    Username
                  </span>
                  <input
                    value={parsedConnection.username ?? ""}
                    onChange={(event) =>
                      updateParsedConnection("username", event.target.value)
                    }
                    placeholder={PROVIDER_DEFAULTS[provider].usernamePlaceholder}
                    className="w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-200"
                  />
                </label>
                <label className="block md:col-span-2">
                  <span className="mb-2 block text-sm font-medium text-slate-700">
                    Password
                  </span>
                  <input
                    type="password"
                    value={parsedConnection.password ?? ""}
                    onChange={(event) =>
                      updateParsedConnection("password", event.target.value)
                    }
                    placeholder="********"
                    className="w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-200"
                  />
                </label>
              </div>
            ) : (
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-slate-700">
                  Connection String
                </span>
                <input
                  value={connectionInput}
                  onChange={(event) => handleConnectionStringChange(event.target.value)}
                  placeholder={`${PROVIDER_DEFAULTS[provider].protocol}//username:password@localhost:${PROVIDER_DEFAULTS[provider].port}/database`}
                  className="w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-200"
                />
                <p className="mt-1 text-sm text-slate-500">
                  Format: {PROVIDER_DEFAULTS[provider].protocol}
                  {"//"}username:password@host:{PROVIDER_DEFAULTS[provider].port}/database
                </p>
              </label>
            )}

            {connectionStatus ? (
              <div
                className={`rounded-md border px-4 py-3 text-sm ${
                  connectionStatus.kind === "success"
                    ? "border-green-200 bg-green-50 text-green-800"
                    : "border-red-200 bg-red-50 text-red-700"
                }`}
              >
                <div className="flex items-center gap-2">
                  {connectionStatus.kind === "success" ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Database className="h-4 w-4" />
                  )}
                  <span>{connectionStatus.message}</span>
                </div>
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => void handleTestConnection()}
                disabled={testingConnection || !connectionInput}
                className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {testingConnection ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Database className="h-4 w-4" />
                )}
                Test Connection
              </button>
              <button
                onClick={() => void handleLoadSchema()}
                disabled={schemaLoading || !connectionInput}
                className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 disabled:opacity-50"
              >
                {schemaLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCcw className="h-4 w-4" />
                )}
                Load Schema
              </button>
              <button
                onClick={() => void handleBurstCache()}
                disabled={cacheBursting || !connectionInput}
                className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 disabled:opacity-50"
              >
                {cacheBursting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCcw className="h-4 w-4" />
                )}
                Burst Cache
              </button>
              <button
                onClick={() => void handleSave()}
                disabled={saving || !connectionInput}
                className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save Connection
              </button>
            </div>
          </div>
        </section>
      ) : (
        <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-slate-900">Schema Access</h3>
            <p className="mt-1 text-sm text-slate-500">
              Configure table and column access separately for admins and members.
            </p>
          </div>

          <div className="border-b border-slate-200 px-6 py-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-1">
                {SCHEMA_ACCESS_ROLES.map((role) => {
                  const enabledTableCount = schemaTablesByRole[role].filter((table) => table.enabled)
                    .length;
                  const Icon = role === "admin" ? Shield : Users;

                  return (
                    <button
                      key={role}
                      onClick={() => setActiveSchemaRole(role)}
                      className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${
                        activeSchemaRole === role
                          ? "bg-white text-slate-900 shadow-sm"
                          : "text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {getRoleLabel(role)}
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">
                        {enabledTableCount}
                      </span>
                    </button>
                  );
                })}
              </div>

              <input
                value={schemaSearch}
                onChange={(event) => setSchemaSearch(event.target.value)}
                placeholder={`Search ${getRoleLabel(activeSchemaRole).toLowerCase()} tables or columns`}
                className="w-full max-w-sm rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-200"
              />
            </div>

            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              <span className="font-medium text-slate-900">{getRoleLabel(activeSchemaRole)}:</span>{" "}
              {activeSchemaRole === "admin"
                ? "select the tables and columns workspace admins can query."
                : "select the restricted subset available to regular members."}
            </div>
          </div>

          <div className="divide-y divide-slate-200">
            {visibleTables.length === 0 ? (
              <div className="px-6 py-8 text-sm text-slate-500">
                {currentRoleTables.length === 0
                  ? "Load the database schema from the Connection tab first."
                  : "No tables or columns match this search."}
              </div>
            ) : (
              visibleTables.map((table) => {
                const expanded = expandedTables.includes(`${activeSchemaRole}:${table.name}`);
                const visibleColumns = getVisibleColumns(table);

                return (
                  <div key={`${activeSchemaRole}:${table.name}`} className="px-6 py-4">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={table.enabled}
                        onChange={(event) =>
                          toggleTableEnabled(table.name, event.target.checked)
                        }
                        className="mt-1 h-4 w-4"
                      />
                      <div className="min-w-0 flex-1">
                        <button
                          onClick={() => toggleTableExpanded(table.name)}
                          className="flex w-full items-start justify-between gap-4 text-left"
                        >
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium text-slate-900">{table.name}</span>
                              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] uppercase tracking-[0.12em] text-slate-500">
                                {table.type.toLowerCase()}
                              </span>
                            </div>
                            <p className="mt-1 text-sm text-slate-500">
                              {table.columns.filter((column) => column.enabled).length}/
                              {table.columns.length} columns enabled
                            </p>
                          </div>
                          <ArrowLeft
                            className={`mt-1 h-4 w-4 rotate-180 text-slate-400 transition-transform ${
                              expanded ? "-rotate-90" : ""
                            }`}
                          />
                        </button>

                        {expanded ? (
                          <div className="mt-4 overflow-hidden rounded-md border border-slate-200">
                            <div className="grid grid-cols-[minmax(240px,1.4fr)_180px_240px] gap-4 bg-slate-50 px-4 py-3 text-xs font-medium uppercase tracking-[0.12em] text-slate-500">
                              <div>Column</div>
                              <div>Type</div>
                              <div>Capabilities</div>
                            </div>
                            {visibleColumns.map((column) => (
                              <div
                                key={`${table.name}:${column.name}`}
                                className="grid grid-cols-[minmax(240px,1.4fr)_180px_240px] gap-4 border-t border-slate-200 px-4 py-3"
                              >
                                <label className="flex min-w-0 items-start gap-3 text-sm text-slate-800">
                                  <input
                                    type="checkbox"
                                    checked={column.enabled}
                                    onChange={(event) =>
                                      toggleColumnEnabled(
                                        table.name,
                                        column.name,
                                        event.target.checked,
                                      )
                                    }
                                    className="mt-1"
                                  />
                                  <div className="min-w-0">
                                    <div className="break-all font-mono text-[13px] text-slate-900">
                                      {column.name || "(unnamed column)"}
                                    </div>
                                    <div className="mt-1 text-xs text-slate-500">
                                      {column.description?.trim() || "Column access"}
                                    </div>
                                  </div>
                                </label>
                                <div className="text-sm text-slate-500">
                                  <div className="font-medium text-slate-700">
                                    {column.rawDataType}
                                  </div>
                                  <div className="text-xs uppercase tracking-[0.12em] text-slate-400">
                                    {column.dataType}
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                                  <label className="flex items-center gap-1">
                                    <input
                                      type="checkbox"
                                      checked={column.filterable ?? false}
                                      onChange={(event) =>
                                        updateColumnFlag(
                                          table.name,
                                          column.name,
                                          "filterable",
                                          event.target.checked,
                                        )
                                      }
                                    />
                                    Filter
                                  </label>
                                  <label className="flex items-center gap-1">
                                    <input
                                      type="checkbox"
                                      checked={column.searchable ?? false}
                                      onChange={(event) =>
                                        updateColumnFlag(
                                          table.name,
                                          column.name,
                                          "searchable",
                                          event.target.checked,
                                        )
                                      }
                                    />
                                    Search
                                  </label>
                                  <label className="flex items-center gap-1">
                                    <input
                                      type="checkbox"
                                      checked={column.sortable ?? false}
                                      onChange={(event) =>
                                        updateColumnFlag(
                                          table.name,
                                          column.name,
                                          "sortable",
                                          event.target.checked,
                                        )
                                      }
                                    />
                                    Sort
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>
      )}
    </div>
  );
}
